import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Button } from '@/components/ui/button';
import { archiveOrSoftDeleteSchedule, getSchool } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { ClassTimesTable } from '@/routes/_layout/orgs/schools/-components/class-times-table';
import { CreateEditClassTime } from '@/routes/_layout/orgs/schools/-components/create-edit-class-time';
import { CreateEditSchedule } from '@/routes/_layout/scheduling/schedules/-components/create-edit-schedule';
import { formatScheduleBreak, formatScheduleDates } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { formatPlainDate, isAirAdmin } from '@airreading/common';
import { ClassTime, Schedule, SchoolWithOrgAndScheduleEdlinkIdentifiable } from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit, EllipsisVerticalIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { validateRouteRole } from '@/lib/routing';
import { schoolDetailsTabs } from '../-components/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/hooks/use-toast';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/schedules')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolSchedulesPage,
});

interface AddEditScheduleProps {
  schedule: Schedule;
  schoolData: SchoolWithOrgAndScheduleEdlinkIdentifiable;
  afterSave: () => void;
}

interface AddEditClassTimeProps {
  classTime: ClassTime;
  schoolTimeZone: string;
  afterSave: () => void;
}

function AddEditSchedule({ schedule, schoolData, afterSave }: AddEditScheduleProps) {
  const mode = schedule.id ? 'edit' : 'create';
  const button =
    mode === 'create' ? (
      <Button>Add Schedule</Button>
    ) : (
      <Button size="sm" icon={Edit} variant="secondary">
        Edit
      </Button>
    );
  return (
    <CreateEditSchedule
      from={{
        fromSchedule: { ...schedule, school: schoolData },
        mode,
      }}
      trigger={button}
      afterSave={afterSave}
      modalTitle={mode === 'edit' ? 'Edit schedule' : 'Create schedule'}
    />
  );
}

function AddEditClassTimes({ classTime, schoolTimeZone, afterSave }: AddEditClassTimeProps) {
  const mode = classTime.id ? 'edit' : 'create';
  const button = <Button size="sm">Add class times</Button>;
  return (
    <CreateEditClassTime
      from={{
        fromClassTime: classTime,
        mode,
      }}
      trigger={button}
      afterSave={afterSave}
      schoolTimeZone={schoolTimeZone}
    />
  );
}

function SchoolSchedulesPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load school. Please try again.',
          back: {
            backLink: {
              to: '/orgs',
            },
          },
        };
      }}
    >
      <SchoolSchedules />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolSchedules() {
  const { schoolId } = Route.useParams();
  const { toast } = useToast();
  const { data } = useSuspenseQuery(getSchool(schoolId));
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: archiveOrSoftDeleteSchedule,
  });

  const schedules = data.schedules
    .filter((s) => s.schoolId === data.id)
    .sort((a, b) => Temporal.PlainDate.compare(b.startPlainDate, a.startPlainDate));
  const [refreshKeys, setRefreshKeys] = useState<Record<string, number>>({});
  const bumpRefreshKeyFor = (scheduleId: string) => {
    setRefreshKeys((prev) => ({
      ...prev,
      [scheduleId]: (prev[scheduleId] ?? 0) + 1,
    }));
  };
  const refetchSchool = useCallback(() => {
    queryClient.invalidateQueries(getSchool(schoolId));
    queryClient.invalidateQueries({ queryKey: ['AVAILABLE_COACHES'] });
  }, [queryClient, schoolId]);

  const emptySchedule: Schedule = {
    id: '',
    name: '',
    timeZone: '',
    practicePlainDate: null,
    diagnosticPlainDate: null,
    schoolId: data.id,
    status: 'open' as const,
    startPlainDate: Temporal.Now.plainDateISO(),
    endPlainDate: Temporal.Now.plainDateISO(),
    scheduleBreaks: [],
    school: data,
    classTimes: [],
  };

  const onArchiveOrDeleteSchedule = async (scheduleId: string, status: 'archived' | 'deleted') => {
    try {
      await mutateAsync({
        id: scheduleId,
        data: { status },
      });
      refetchSchool();
      toast({
        title: `Schedule ${status} successfully`,
      });
    } catch (error) {
      toast({
        title: `Failed to ${status} schedule`,
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const emptyClassTime: ClassTime = {
    id: '',
    scheduleId: '',
    type: 'recurring',
    meetingBlocks: [],
    studentSeats: 0,
    schedule: emptySchedule,
  };

  const getAfterSaveCallback = (scheduleId?: string) => () => {
    refetchSchool();
    if (scheduleId) bumpRefreshKeyFor(scheduleId);
  };

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: data.name,
      }}
      tabs={schoolDetailsTabs({ schoolId: data.id })}
    >
      <InfoCard
        headerText="Schedules"
        maxItemsPerRow={1}
        headerActions={
          <AddEditSchedule schedule={emptySchedule} schoolData={data} afterSave={getAfterSaveCallback()} />
        }
      >
        {schedules.map((schedule) => {
          const { id, name, practicePlainDate, diagnosticPlainDate, scheduleBreaks } = schedule;
          const elementId = `${id}-${refreshKeys[id] ?? 0}`;
          const regularDates = formatScheduleDates(schedule);
          const practiceDate = practicePlainDate ? formatPlainDate(practicePlainDate) : '';
          const diagnosticDate = diagnosticPlainDate ? formatPlainDate(diagnosticPlainDate) : '';

          return (
            <InfoCard
              key={`schedule-${elementId}`}
              headerText={name}
              headerActions={
                <div className="flex gap-2">
                  <AddEditClassTimes
                    classTime={{ ...emptyClassTime, scheduleId: id }}
                    schoolTimeZone={data.org.timeZone}
                    afterSave={getAfterSaveCallback()}
                  />
                  <AddEditSchedule schedule={schedule} schoolData={data} afterSave={getAfterSaveCallback(id)} />
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" size={'sm'}>
                        <EllipsisVerticalIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuGroup>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!schedule.canDelete}>
                              Delete
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete schedule</AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogDescription>
                              Are you sure you want to Delete this schedule? This action cannot be undone.
                            </AlertDialogDescription>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction asChild>
                                <Button
                                  variant="destructive"
                                  disabled={!schedule.canDelete}
                                  onClick={() => {
                                    onArchiveOrDeleteSchedule(schedule.id, 'deleted');
                                  }}
                                >
                                  Delete
                                </Button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!schedule.canArchive}>
                              Archive
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Archive schedule</AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogDescription>
                              Are you sure you want to archive this schedule?
                            </AlertDialogDescription>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction asChild>
                                <Button
                                  disabled={!schedule.canArchive}
                                  onClick={() => {
                                    onArchiveOrDeleteSchedule(schedule.id, 'archived');
                                  }}
                                >
                                  Archive
                                </Button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              }
            >
              <InfoKeyValue label="Regular class dates" value={regularDates} />
              <InfoKeyValue label="Practice date" value={practiceDate} />
              <InfoKeyValue label="Diagnostic date" value={diagnosticDate} />
              <InfoKeyValue
                label="Break"
                value={
                  <>
                    {scheduleBreaks.map((scheduleBreak, i) => {
                      return <div key={`schedule-break-${elementId}-${i}`}>{formatScheduleBreak(scheduleBreak)}</div>;
                    })}
                  </>
                }
              />
              <InfoKeyValue
                label="Student seats"
                value={schedule.classTimes.reduce((acc, classTime) => acc + classTime.studentSeats, 0)}
              />
              <div className="w-full col-span-4">
                <ClassTimesTable
                  scheduleId={id}
                  classTimeTypes={['recurring']}
                  schoolTimeZone={data.org.timeZone}
                  afterSave={refetchSchool}
                />
              </div>
            </InfoCard>
          );
        })}
      </InfoCard>
    </InfoPage>
  );
}
